const UTM_STORAGE_KEY = "lead_intake_utm";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_MIN_DIGITS = 7;
const instances = new WeakMap();

function normalizePhone(raw) {
  if (!raw) return null;
  const trimmed = raw.trim();
  const digits = trimmed.replace(/[^\d]/g, "");
  const withPlus = trimmed.startsWith("+") ? "+" + digits : digits;
  if (!withPlus.startsWith("+") || digits.length < PHONE_MIN_DIGITS) return null;
  return withPlus;
}

function readStoredUtm() {
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
}

function persistUtmFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const trackedKeys = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "gclid",
    "fbclid",
  ];

  const next = { ...readStoredUtm() };
  let changed = false;
  trackedKeys.forEach((key) => {
    const value = params.get(key);
    if (value) {
      next[key] = value;
      changed = true;
    }
  });

  if (!changed) return;
  try {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    // Ignore storage failures.
  }
}

class LeadIntakeFormController {
  constructor(form) {
    this.form = form;
    this.submitButton = form.querySelector('button[type="submit"]');
    this.status = form.querySelector("[data-form-status]");
    this.submitting = false;
    this.renderedAt = Date.now();

    this.config = {
      endpoint: (form.dataset.endpoint || "").trim(),
      source: (form.dataset.source || "").trim(),
      campaign: (form.dataset.campaign || "").trim(),
      setEmailConsent: form.dataset.setEmailConsent === "true",
      language: (form.dataset.language || "de").trim(),
      timeoutMs: parseInt(form.dataset.timeoutMs || "10000", 10),
      minSubmitMs: parseInt(form.dataset.minSubmitMs || "2000", 10),
      messages: {
        success: form.dataset.successMessage || "Erfolgreich gesendet.",
        successFallback:
          form.dataset.fallbackSuccessMessage ||
          "Gesendet. Die Verarbeitung im Hintergrund kann verzögert sein.",
        error:
          form.dataset.errorMessage ||
          "Senden fehlgeschlagen. Bitte versuche es erneut.",
        unsupported:
          form.dataset.unsupportedMessage ||
          "Formular-Konfiguration unvollständig.",
      },
      validation: {
        required: form.dataset.validationRequired || "Bitte ausfüllen.",
        email: form.dataset.validationEmail || "Ungültige E-Mail-Adresse.",
        salutation:
          form.dataset.validationSalutation || "Bitte Anrede auswählen.",
        phone:
          form.dataset.validationPhone ||
          "Bitte Telefonnummer mit Ländervorwahl eingeben, z.B. +41 79 123 45 67",
      },
    };

    this.normalizedPhone = null;

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleInput = this.handleInput.bind(this);
  }

  async init() {
    this.form.addEventListener("submit", this.handleSubmit);
    this.form.addEventListener("input", this.handleInput);
    this.form.addEventListener("change", this.handleInput);
  }

  destroy() {
    this.form.removeEventListener("submit", this.handleSubmit);
    this.form.removeEventListener("input", this.handleInput);
    this.form.removeEventListener("change", this.handleInput);
  }

  handleInput(event) {
    const field = event.target?.name;
    if (field) this.clearFieldError(field);
    this.clearStatus();
  }

  setStatus(message, type) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.classList.remove("is-success", "is-error");
    if (type === "success") this.status.classList.add("is-success");
    if (type === "error") this.status.classList.add("is-error");
  }

  clearStatus() {
    if (!this.status) return;
    this.status.textContent = "";
    this.status.classList.remove("is-success", "is-error");
  }

  setFieldError(field, message) {
    const errorEl = this.form.querySelector(`[data-field-error="${field}"]`);
    const input = this.form.querySelector(`[name="${field}"]`);
    if (errorEl) errorEl.textContent = message;
    if (input) input.setAttribute("aria-invalid", "true");
  }

  clearFieldError(field) {
    const errorEl = this.form.querySelector(`[data-field-error="${field}"]`);
    const input = this.form.querySelector(`[name="${field}"]`);
    if (errorEl) errorEl.textContent = "";
    if (input) input.removeAttribute("aria-invalid");
  }

  clearAllErrors() {
    this.form.querySelectorAll("[data-field-error]").forEach((el) => {
      el.textContent = "";
    });
    this.form
      .querySelectorAll("[aria-invalid='true']")
      .forEach((el) => el.removeAttribute("aria-invalid"));
  }

  setSubmitState(isSubmitting) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isSubmitting;
    if (isSubmitting) {
      this.submitButton.setAttribute("aria-busy", "true");
    } else {
      this.submitButton.removeAttribute("aria-busy");
    }
  }

  getTrimmedValue(name) {
    const input = this.form.querySelector(`[name="${name}"]`);
    return (input?.value || "").trim();
  }

  validate() {
    const errors = {};
    const salutationRaw = this.getTrimmedValue("salutationId");
    const salutationId = parseInt(salutationRaw, 10);

    if (!Number.isInteger(salutationId) || salutationId <= 0) {
      errors.salutationId = this.config.validation.salutation;
    }
    [
      "firstName",
      "lastName",
      "street",
      "zipcode",
      "city",
      "receiptNumber",
    ].forEach((field) => {
      if (!this.getTrimmedValue(field)) {
        errors[field] = this.config.validation.required;
      }
    });

    const email = this.getTrimmedValue("email").toLowerCase();
    if (!email) {
      errors.email = this.config.validation.required;
    } else if (!EMAIL_PATTERN.test(email)) {
      errors.email = this.config.validation.email;
    }

    const phoneRaw = this.getTrimmedValue("phoneMobile");
    this.normalizedPhone = null;
    if (!phoneRaw) {
      errors.phoneMobile = this.config.validation.required;
    } else {
      this.normalizedPhone = normalizePhone(phoneRaw);
      if (!this.normalizedPhone) {
        errors.phoneMobile = this.config.validation.phone;
      }
    }

    if (!this.config.endpoint || !this.config.source) {
      errors.form = this.config.messages.unsupported;
    } else {
      try {
        // Validates endpoint format early and avoids opaque fetch failures.
        new URL(this.config.endpoint, window.location.origin);
      } catch (error) {
        errors.form = this.config.messages.unsupported;
      }
    }

    const honeypot = this.getTrimmedValue("website");
    if (honeypot) {
      errors.form = this.config.messages.error;
    }

    if (Date.now() - this.renderedAt < this.config.minSubmitMs) {
      errors.form = this.config.messages.error;
    }

    return errors;
  }

  buildPayload() {
    const payload = {
      salutationId: parseInt(this.getTrimmedValue("salutationId"), 10),
      firstName: this.getTrimmedValue("firstName"),
      lastName: this.getTrimmedValue("lastName"),
      street: this.getTrimmedValue("street"),
      zipcode: this.getTrimmedValue("zipcode"),
      city: this.getTrimmedValue("city"),
      receiptNumber: this.getTrimmedValue("receiptNumber"),
      country: "CH",
      email: this.getTrimmedValue("email").toLowerCase(),
      phoneMobile: this.normalizedPhone,
      source: this.config.source,
      language: this.config.language || "de",
    };

    if (this.config.campaign) payload.campaign = this.config.campaign;
    if (this.config.setEmailConsent) payload.emailConsent = true;

    const utm = readStoredUtm();
    Object.keys(utm).forEach((key) => {
      if (utm[key]) payload[key] = utm[key];
    });

    return payload;
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.submitting) return;
    this.setSubmitState(false);

    this.clearAllErrors();
    this.clearStatus();

    const errors = this.validate();
    Object.keys(errors).forEach((key) => {
      if (key === "form") return;
      this.setFieldError(key, errors[key]);
    });

    if (errors.form) {
      this.setStatus(errors.form, "error");
      return;
    }
    if (Object.keys(errors).length > 0) return;

    this.submitting = true;
    this.setSubmitState(true);

    const controller = new AbortController();
    const timeout = window.setTimeout(
      () => controller.abort(),
      Number.isNaN(this.config.timeoutMs) ? 10000 : this.config.timeoutMs
    );

    try {
      const response = await fetch(this.config.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(this.buildPayload()),
        signal: controller.signal,
      });

      let data = null;
      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }

      if (response.status === 200 && data?.klaviyo_ok === true) {
        this.setStatus(this.config.messages.success, "success");
        this.form.reset();
      } else if (response.status === 502 && data?.ok === true) {
        this.setStatus(this.config.messages.successFallback, "success");
        this.form.reset();
      } else {
        this.setStatus(this.config.messages.error, "error");
      }

    } catch (error) {
      this.setStatus(this.config.messages.error, "error");
    } finally {
      window.clearTimeout(timeout);
      this.submitting = false;
      this.setSubmitState(false);
    }
  }
}

function initInContainer(container) {
  container.querySelectorAll("[data-lead-intake-form]").forEach((form) => {
    if (instances.has(form)) return;
    const instance = new LeadIntakeFormController(form);
    instances.set(form, instance);
    instance.init();
  });
}

function destroyInContainer(container) {
  container.querySelectorAll("[data-lead-intake-form]").forEach((form) => {
    const instance = instances.get(form);
    if (!instance) return;
    instance.destroy();
    instances.delete(form);
  });
}

persistUtmFromUrl();
initInContainer(document);

document.addEventListener("shopify:section:load", (event) => {
  initInContainer(event.target);
});

document.addEventListener("shopify:section:unload", (event) => {
  destroyInContainer(event.target);
});
